import React, {
  cloneElement,
  HTMLProps,
  ReactElement,
  RefAttributes,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ContextualMenu,
  DirectionalHint,
  IContextualMenuItem,
  IContextualMenuStyleProps,
  IContextualMenuStyles,
  IStyleFunctionOrObject,
} from '@fluentui/react';
import { FloatingLayerProvider } from '../../FloatingManager';

/**
 * Quick guide before you sh*t yourself in the leg:
 * 1. We use this component ONLY when we have sub menus(aka multi level menus).
 * 2. You always have to provide an array of [items]. For each item
 *    "key" and "onRenderContent" are required.
 * 3. We use our custom component (ContextualMenuOption) for "onRenderContent".
 *    If you use smth different - good luck with styling.
 * 4. Example of simple menu option (no sub menus):
 *    {
 *      key: 'unique-key',
 *      onRenderContent: () => (
 *        <ContextualMenuOption label={'Option'} onClick={handleClick} />
 *      ),
 *    }
 * 5. Example of menu option with sub menu:
 *    {
 *      key: 'unique-with-sub',
 *      onRenderContent: () => (
 *        <ContextualMenuOption label={'Option'} hasSubMenus={true} />
 *      ),
 *      subMenuProps: {
 *        items: [
 *             {
 *              key: 'unique-key',
 *              onRenderContent: () => (
 *                <ContextualMenuOption label={'N-Option'} onClick={handleNClick} />
 *              ),
 *          },
 *        ],
 *      },
 *    }
 * 6. Please check ContextualMenuOption component before reinventing the wheel
 */

// IMPORTANT: Not obvious at first but when Contextual menu is opened it will
// render an extra span with class 'ms-layer' next to trigger element.
// In order to avoid UI glitches(for example jumps) - consider wrapping
// menu in extra div

// create shortcut type from the second argument of the IContextualMenuItem['onRender'] optional function
export type IDismissContextualMenuCallback = Parameters<
  Required<Pick<IContextualMenuItem, 'onRender'>>['onRender']
>[1];

interface ITriggerProps {
  ref: RefAttributes<HTMLDivElement>['ref'];
  onClick: HTMLProps<HTMLDivElement>['onClick'];
  'data-open': boolean;
}

interface IProps {
  trigger: ReactElement<ITriggerProps>;
  items: IContextualMenuItem[];
  style?: IStyleFunctionOrObject<
    IContextualMenuStyleProps,
    IContextualMenuStyles
  >;
  directionalHint?: DirectionalHint;
  onClose?: () => void;
  onMenuOpened?: () => void;
  allowTriggerOnClick?: boolean;
  disabled?: boolean;
}

export const dropdownStyle: IProps['style'] = {
  root: {
    minWidth: 'initial',
  },
};

interface IContextualMenuContext {
  disableClose: (disableClose: boolean) => void;
}

export const ContextualMenuContext = React.createContext<
  IContextualMenuContext | undefined
>(undefined);
ContextualMenuContext.displayName = 'ContextualMenuContext';

const KubitContextualMenu = ({
  trigger,
  items,
  style,
  directionalHint,
  onClose,
  onMenuOpened,
  allowTriggerOnClick,
  disabled,
}: IProps) => {
  const triggerRef = useRef(null);

  const disableCloseRef = useRef(false);
  const contextualMenuContextValue = useMemo<IContextualMenuContext>(
    () => ({
      disableClose: (disableClose) => (disableCloseRef.current = disableClose),
    }),
    [],
  );

  const [showContextualMenu, setShowContextualMenu] = useState(false);

  const onHideContextualMenu = useCallback(() => {
    setShowContextualMenu(false);

    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const toggleContextualMenuState = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      if (!allowTriggerOnClick) {
        ev.preventDefault();
      }

      if (showContextualMenu) {
        onHideContextualMenu();
      } else {
        setShowContextualMenu(true);
      }
    },
    [onHideContextualMenu, showContextualMenu, allowTriggerOnClick],
  );

  const triggerElement = cloneElement(trigger, {
    ref: triggerRef,
    onClick: disabled ? undefined : toggleContextualMenuState,
    'data-open': showContextualMenu,
  });

  return (
    <FloatingLayerProvider>
      <ContextualMenuContext.Provider value={contextualMenuContextValue}>
        {triggerElement}
        {showContextualMenu && (
          <ContextualMenu
            items={items}
            isBeakVisible={false}
            beakWidth={8}
            gapSpace={2}
            alignTargetEdge={true}
            target={triggerRef}
            onItemClick={onHideContextualMenu}
            onDismiss={onHideContextualMenu}
            onMenuOpened={onMenuOpened}
            directionalHint={directionalHint || DirectionalHint.bottomLeftEdge}
            styles={style}
            calloutProps={{
              preventDismissOnEvent: () => disableCloseRef.current,
            }}
          />
        )}
      </ContextualMenuContext.Provider>
    </FloatingLayerProvider>
  );
};

export default KubitContextualMenu;
